import * as PIXI from 'pixi.js';
import { Config } from '../../../video-data/config';
import { TypedSerializableMixin } from '../../serializableMixin';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize,
} from '../../attributes';
import { int2rgba } from '../../../libs/videoData';
import { createLinearGradient } from '../../../libs/gradient';
import { clampCornerRadii } from '../../../libs/roundedRectPath';
import { GradientPoint } from '../../GradientPoint';
import type { DestroyOptions } from '../../../types';

export type BoxGraphicAnimationXDirection = (typeof Config.ALL_ANIMATION_IN_X_DIRECTION)[number];
export type BoxGraphicAnimationYDirection = (typeof Config.ALL_ANIMATION_IN_Y_DIRECTION)[number];

/**
 * Draws a colored bounding box around the entire text(Metrics)
 *
 */
export class BoxGraphic extends TypedUpdateFlowMixin(TypedSerializableMixin(PIXI.Graphics)) {
    override _classType = 'BoxGraphic';
    override _name = Config.BOX_GRAPHIC;

    @NumberAttribute({ animatable: false })
    accessor positionX = 150;
    @NumberAttribute({ animatable: false })
    accessor positionY = 150;
    @NumberAttribute()
    accessor boxWidth = 50;
    @NumberAttribute()
    accessor boxHeight = 50;
    @NumberAttribute({ min: 0, animatable: false })
    accessor topLeftRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    accessor topRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    accessor bottomRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    accessor bottomLeftRadius = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingTop = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingBottom = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingLeft = 0;
    @NumberAttribute({ animatable: false })
    accessor paddingRight = 0;
    @ColorAttribute()
    accessor strokeColor = 16777215;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fillColor = 16777215;
    @AlphaAttribute()
    accessor fillAlpha = 1;
    @NumberAttribute({ min: 0 })
    accessor progressYValue: number = 1.0;
    @NumberAttribute({ min: 0 })
    accessor progressXValue = 1.0;
    @StringAttribute({ choices: Config.ALL_ANIMATION_IN_X_DIRECTION })
    accessor horizontalDirection: BoxGraphicAnimationXDirection = Config.RIGHT_ANIMATION_DIRECTION;
    @StringAttribute({ choices: Config.ALL_ANIMATION_IN_Y_DIRECTION })
    accessor verticalDirection: BoxGraphicAnimationYDirection = Config.CENTER_ANIMATION_DIRECTION;

    @StringAttribute({ choices: ['solid', 'linear'], animatable: false })
    accessor fillType: 'solid' | 'linear' = 'solid';

    gradientStartPoint = new GradientPoint({ parent: this, x: 0, y: 0.5, color: 0xff0000 });
    gradientEndPoint = new GradientPoint({ parent: this, x: 1, y: 0.5, color: 0x0000ff });

    #canvas: HTMLCanvasElement | null = null;
    #gradientTexture: PIXI.Texture | null = null;
    #gradientTextureKey: string | null = null;

    #calculateDimensions() {
        const totalHeight = this.boxHeight + this.paddingBottom + this.paddingTop;
        const totalWidth = this.boxWidth + this.paddingLeft + this.paddingRight;
        const percentageHeight = totalHeight * this.progressYValue;
        const percentageWidth = totalWidth * this.progressXValue;
        let positionX = this.positionX;
        let positionY = this.positionY;

        switch (this.horizontalDirection) {
            case 'right':
                // pass
                break;
            case 'left':
                positionX += (1 - this.progressXValue) * totalWidth;
                break;
            case 'center':
                positionX += (totalWidth - percentageWidth) / 2;
                break;
            default:
                throw Error(`Unknown ${this.horizontalDirection}`);
        }

        switch (this.verticalDirection) {
            case 'bottom':
                // pass
                break;
            case 'top':
                positionY += (1 - this.progressYValue) * totalHeight;
                break;
            case 'center':
                positionY += (totalHeight - percentageHeight) / 2;
                break;
            default:
                throw Error(`Unknown ${this.verticalDirection}`);
        }

        return {
            x: positionX,
            y: positionY,
            width: percentageWidth,
            height: percentageHeight,
        };
    }

    #calculateShape(width: number, height: number) {
        return clampCornerRadii(
            {
                topLeftRadius: this.topLeftRadius,
                topRightRadius: this.topRightRadius,
                bottomRightRadius: this.bottomRightRadius,
                bottomLeftRadius: this.bottomLeftRadius,
            },
            width,
            height,
        );
    }

    #createGradientTexture(width: number, height: number) {
        if (!this.#canvas) {
            this.#canvas = document.createElement('canvas');
        }
        const shapeWidth = Math.max(1, Math.round(width));
        const shapeHeight = Math.max(1, Math.round(height));

        const start = this.gradientStartPoint;
        const end = this.gradientEndPoint;

        const key = [
            shapeWidth,
            shapeHeight,
            start.x,
            start.y,
            start.color,
            start.alpha,
            end.x,
            end.y,
            end.color,
            end.alpha,
        ].join(':');
        if (this.#gradientTexture && this.#gradientTextureKey === key) {
            return this.#gradientTexture;
        }

        // +1 px padding: UV at the shape edge becomes width/(width+1) < 1.0,
        // preventing wrap-around artifacts regardless of the texture's wrapMode.
        const canvasWidth = shapeWidth + 1;
        const canvasHeight = shapeHeight + 1;
        this.#canvas.width = canvasWidth;
        this.#canvas.height = canvasHeight;

        const context = this.#canvas.getContext('2d');
        if (!context) return null;

        context.clearRect(0, 0, canvasWidth, canvasHeight);

        const gradient = createLinearGradient(context, {
            startX: start.x * shapeWidth,
            startY: start.y * shapeHeight,
            endX: end.x * shapeWidth,
            endY: end.y * shapeHeight,
            startStop: int2rgba(start.color, start.alpha),
            endStop: int2rgba(end.color, end.alpha),
        });

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvasWidth, canvasHeight);

        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
        }
        this.#gradientTexture = PIXI.Texture.from(this.#canvas);
        this.#gradientTextureKey = key;
        return this.#gradientTexture;
    }

    #drawShape(
        x: number,
        y: number,
        width: number,
        height: number,
        topLeftRadius: number,
        topRightRadius: number,
        bottomLeftRadius: number,
        bottomRightRadius: number,
    ) {
        this.clear();
        this.lineStyle(this.strokeWidth, this.strokeColor, this.strokeAlpha);

        const fillColor = this.fillType === 'solid' ? this.fillColor : 0xffffff;
        const fillAlpha = this.fillType === 'solid' ? this.fillAlpha : 1;

        if (this.fillType === 'linear') {
            const texture = this.#createGradientTexture(width, height);
            if (texture) {
                // beginTextureFill inverts the matrix before applying it, so pass
                // the positive offset (PIXI inverts to -x, -y) to align texture top-left
                // with the shape's top-left corner.
                const matrix = new PIXI.Matrix();
                matrix.translate(x, y);
                this.beginTextureFill({ texture, matrix });
            } else {
                this.beginFill(fillColor, fillAlpha);
            }
        } else {
            this.beginFill(fillColor, fillAlpha);
        }

        // draw a simple rectangle if no rounded corners are required
        if (
            topLeftRadius === 0 &&
            topRightRadius === 0 &&
            bottomRightRadius === 0 &&
            bottomLeftRadius === 0
        ) {
            this.drawRect(x, y, width, height);
            this.endFill();
            return;
        }

        // draw rectangle with one or more rounded corners
        if (topLeftRadius < 1) {
            this.lineTo(x, y);
        } else {
            this.arc(x + topLeftRadius, y + topLeftRadius, topLeftRadius, Math.PI, -Math.PI / 2);
        }

        if (topRightRadius < 1) {
            this.lineTo(x + width, y);
        } else {
            this.arc(
                x + width - topRightRadius,
                y + topRightRadius,
                topRightRadius,
                -Math.PI / 2,
                0,
            );
        }

        if (bottomRightRadius < 1) {
            this.lineTo(x + width, y + height);
        } else {
            this.arc(
                x + width - bottomRightRadius,
                y + height - bottomRightRadius,
                bottomRightRadius,
                0,
                Math.PI / 2,
            );
        }

        if (bottomLeftRadius < 1) {
            this.lineTo(x, y + height);
        } else {
            this.arc(
                x + bottomLeftRadius,
                y + height - bottomLeftRadius,
                bottomLeftRadius,
                Math.PI / 2,
                Math.PI,
            );
        }
        this.closePath();
        this.endFill();
    }

    override _update() {
        const { x, y, width, height } = this.#calculateDimensions();
        const { topLeftRadius, topRightRadius, bottomLeftRadius, bottomRightRadius } =
            this.#calculateShape(width, height);
        this.#drawShape(
            x,
            y,
            width,
            height,
            topLeftRadius,
            topRightRadius,
            bottomLeftRadius,
            bottomRightRadius,
        );
    }

    override get propertiesToSerialize(): Array<string> {
        return getAttributesToSerialize(this).concat(['gradientStartPoint', 'gradientEndPoint']);
    }

    override destroy(options?: DestroyOptions) {
        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
            this.#gradientTexture = null;
            this.#gradientTextureKey = null;
        }
        super.destroy(options);
    }
}
